import { useAnalytics } from '@databases/analytics';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseFieldType } from '@databases/types/database';
import React, { type FunctionComponent, useCallback } from 'react';

import TextValuePopoverEdit from '../TextValuePopoverEdit';
import { type EditComponentProps } from '../types';

import { updateJiraSummary } from './api';
import { useJiraIssueData, useInputValue } from './hooks';

const JiraSummaryValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.JIRA_ISSUE_DETAILS>
> = ({
  contentId,
  entryId,
  field,
  onFocus,
  onBlur,
  isSeamless,
  autoFocus,
  isBulkEdit,
  isRenderedInSidePanel,
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const analytics = useAnalytics();

  const { issueRef, resolvedIssue, computedSummaryText, abortControllerRef } =
    useJiraIssueData(contentId, entryId, field);
  const { value, setValue } = useInputValue(computedSummaryText);
  const isSummaryField = field.settings.jiraField === 'summary';

  const handleSubmit = useCallback(async () => {
    if (!issueRef || !resolvedIssue) {
      return;
    }
    await updateJiraSummary({
      issueRef,
      resolvedIssue,
      newSummary: value,
      originalSummary: computedSummaryText,
      dispatch,
      abortSignal: abortControllerRef.current?.signal,
      contentId,
      analytics,
    });
  }, [
    issueRef,
    resolvedIssue,
    value,
    computedSummaryText,
    dispatch,
    abortControllerRef,
    contentId,
    analytics,
  ]);

  const handleStopEdit = useCallback(() => {
    void handleSubmit();
    onBlur?.();
  }, [handleSubmit, onBlur]);

  if (!isSummaryField || !issueRef) {
    return null;
  }

  return (
    <TextValuePopoverEdit
      isBulkEdit={isBulkEdit}
      contentId={contentId}
      field={field as unknown as EditComponentProps<DatabaseFieldType.TEXT>['field']}
      value={value as unknown as EditComponentProps<DatabaseFieldType.TEXT>['value']}
      onChange={(newValue) => setValue(newValue as unknown as string)}
      onFocus={onFocus}
      onBlur={handleStopEdit}
      autoFocus={autoFocus}
      isSeamless={isSeamless}
      isRenderedInSidePanel={isRenderedInSidePanel}
    />
  );
};

export default JiraSummaryValueEdit;
